/**
 * Gmail conversation state for Ashley V1.5 single-email review.
 * Reused across turns so Ashley does not re-query Gmail for browsing.
 */

import { EMAIL_PAGE_SIZE, getEmailPage } from "./formatEmailVoiceSummary";

/** @typedef {"browse" | "single_email" | "clarify_sender" | "review_all"} GmailBrowsingMode */

/**
 * @typedef {object} GmailConversationState
 * @property {import("./emailProviderTypes").EmailMessageSummary[]} messages
 * @property {number} offset
 * @property {number} pageSize
 * @property {boolean} afterSummary
 * @property {GmailBrowsingMode} browsingMode
 * @property {import("./emailProviderTypes").EmailMessageSummary[]|null} currentPageSenders
 * @property {import("./emailProviderTypes").EmailMessageSummary|null} selectedEmail
 * @property {string|null} selectedMessageId
 * @property {number|null} selectedOrdinal
 * @property {boolean} awaitingFullRead
 * @property {boolean} afterFullRead
 * @property {string|null} bodyText
 * @property {{ senderKey: string, matches: import("./emailProviderTypes").EmailMessageSummary[] }|null} pendingAmbiguous
 */

/**
 * @param {Partial<GmailConversationState> & { messages?: import("./emailProviderTypes").EmailMessageSummary[] }} [seed]
 * @returns {GmailConversationState}
 */
export function createGmailConversationState(seed = {}) {
  const messages = Array.isArray(seed.messages) ? seed.messages : [];
  const offset = Math.max(0, Number(seed.offset) || 0);
  const pageSize = Math.max(1, Number(seed.pageSize) || EMAIL_PAGE_SIZE);
  const page = getEmailPage(messages, offset, pageSize);

  return {
    messages,
    offset,
    pageSize,
    afterSummary: Boolean(seed.afterSummary),
    browsingMode: seed.browsingMode || "browse",
    currentPageSenders: page,
    selectedEmail: seed.selectedEmail || null,
    selectedMessageId: seed.selectedMessageId || seed.selectedEmail?.id || null,
    selectedOrdinal:
      seed.selectedOrdinal == null ? null : Number(seed.selectedOrdinal),
    awaitingFullRead: Boolean(seed.awaitingFullRead),
    afterFullRead: Boolean(seed.afterFullRead),
    bodyText: seed.bodyText == null ? null : String(seed.bodyText),
    pendingAmbiguous: seed.pendingAmbiguous || null,
  };
}

/**
 * Snapshot of the current page (for spoken lists + selection scope).
 * @param {GmailConversationState} state
 */
export function getActiveBrowsePage(state) {
  if (!state) {
    return [];
  }
  if (state.browsingMode === "review_all") {
    return Array.isArray(state.messages) ? state.messages : [];
  }
  return getEmailPage(
    state.messages,
    state.offset,
    state.pageSize || EMAIL_PAGE_SIZE,
  );
}

/**
 * Select a message and enter single-email mode, preserving page offset.
 * @param {GmailConversationState} state
 * @param {import("./emailProviderTypes").EmailMessageSummary} message
 * @param {number} ordinal 1-based index within the active page
 * @returns {GmailConversationState}
 */
export function selectEmailInState(state, message, ordinal) {
  return createGmailConversationState({
    ...state,
    browsingMode: "single_email",
    selectedEmail: message,
    selectedMessageId: message?.id || null,
    selectedOrdinal: ordinal,
    awaitingFullRead: true,
    afterFullRead: false,
    bodyText: null,
    pendingAmbiguous: null,
    afterSummary: state.afterSummary,
  });
}

/**
 * Return from single-email review to the exact browse page.
 * @param {GmailConversationState} state
 * @returns {GmailConversationState}
 */
export function returnToInboxBrowse(state) {
  return createGmailConversationState({
    messages: state.messages,
    offset: state.offset,
    pageSize: state.pageSize,
    afterSummary: false,
    browsingMode: "browse",
    selectedEmail: null,
    selectedMessageId: null,
    selectedOrdinal: null,
    awaitingFullRead: false,
    afterFullRead: false,
    bodyText: null,
    pendingAmbiguous: null,
  });
}

/**
 * @param {GmailConversationState} state
 * @param {{ senderKey: string, matches: import("./emailProviderTypes").EmailMessageSummary[] }} ambiguous
 * @returns {GmailConversationState}
 */
export function setAmbiguousSenderState(state, ambiguous) {
  return createGmailConversationState({
    ...state,
    browsingMode: "clarify_sender",
    pendingAmbiguous: ambiguous,
    selectedEmail: null,
    selectedMessageId: null,
    selectedOrdinal: null,
    awaitingFullRead: false,
    afterFullRead: false,
    bodyText: null,
  });
}
